import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { isEmptyObject } from '@commons/util/helper/helper';
import { isArray } from 'angular';
import { Subject, Subscription } from 'rxjs';

import { Option, TranslateService, UntilDestroy, untilDestroyed } from '@contact/upgrade';

class ChosenElement {
  content: any;
  selected: boolean;
  chosen: boolean;
  displayed: boolean;
}

@UntilDestroy()
@Component({
  selector: 'cng-chosen-field',
  templateUrl: 'chosen.component.html',
  styleUrls: ['./chosen.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChosenComponent implements OnInit, OnChanges {
  @Input() parenOrderValue: any | any[] | null | undefined;
  @Input() collection: any[] | undefined;
  @Input() htmlId: string;
  @Input() multiple: boolean;
  @Input() fieldPlaceholder: string;
  @Input() resetSwitch: boolean;
  @Input() fieldDisabled: boolean;
  @Output() callback: EventEmitter<any | undefined> = new EventEmitter();
  @Output() keypressCallback: EventEmitter<any> = new EventEmitter();
  @Output() keyupCallback: EventEmitter<any> = new EventEmitter();

  // template related property
  public filter: FormControl = new FormControl('');
  public resultExpanded: boolean;
  public currentIndex: number;
  public elements: ChosenElement[];
  public chosenElement: ChosenElement | ChosenElement[] | undefined;

  private originalPlaceholder: string;
  private currentElem: any;
  private internalChosenElement: Subject<ChosenElement | ChosenElement[] | undefined> = new Subject();
  private chosenElemSubscription: Subscription | undefined;

  constructor(private readonly translateService: TranslateService) {}

  /**
   * Initiation method
   */
  ngOnInit(): void {
    this.fieldPlaceholder = !!this.fieldPlaceholder
      ? this.fieldPlaceholder
      : (this.fieldPlaceholder = this.multiple ? 'commons:basic.chosendefaultplaceholder' : 'commons:basic.chosensingledefaultplaceholder');
    this.originalPlaceholder = this.fieldPlaceholder;
    this.resultExpanded = false;
    this.filter.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.filterCollection(this.elements));
    this.currentIndex = 0;
    this.currentElem = undefined;
    // when internal chosen element was not subscribed before initiation
    if (this.chosenElemSubscription === undefined) {
      this.chosenElemSubscription = this.internalChosenElement
        .asObservable()
        .pipe(untilDestroyed(this))
        .subscribe(newVal => {
          this.onElementChosen(newVal);
        });
    }
    if (this.multiple) {
      this.internalChosenElement.next([]);
    }
  }

  /**
   * OnChanges method to update chosen result and elements list
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    let { ['parenOrderValue']: parentOrder, ['collection']: collection, ['resetSwitch']: resetSwitch } = changes;
    if (!!resetSwitch && !resetSwitch.isFirstChange() && resetSwitch.currentValue !== resetSwitch.previousValue) {
      this.resetAll();
    }
    if (!!collection && collection.previousValue !== collection.currentValue) {
      // each time collection is changed we rebuild and re-filter the elements
      this.elements = this.filterCollection(this.buildChosenElement(collection.currentValue));
      this.filterChosenElement();
    }
    if (!!parentOrder && parentOrder.currentValue !== parentOrder.previousValue) {
      // subscribe in the first change
      if (parentOrder.isFirstChange() && this.chosenElemSubscription === undefined) {
        this.chosenElemSubscription = this.internalChosenElement
          .asObservable()
          .pipe(untilDestroyed(this))
          .subscribe(newValue => {
            this.onElementChosen(newValue);
          });
      }
      // when parent order is undefined or null we call method selectElem
      if (parentOrder.currentValue === undefined || parentOrder.currentValue === null) {
        this.resetAll();
      } else {
        // else we will find the corresponding element in elements list
        let content: any | any[] = parentOrder.currentValue;
        this.multiple = this.multiple || isArray(content);
        if (this.multiple) {
          if (!isArray(content)) {
            content = [content];
          }
          const chosenElem: ChosenElement[] = [];
          for (let chosenContent of content) {
            for (let elem of this.elements) {
              if (chosenContent.name === elem.content.name) {
                // we keep it in status of chosen true
                elem.chosen = true;
                chosenElem.push(elem);
              }
            }
          }
          this.internalChosenElement.next(chosenElem);
        } else {
          for (let elem of this.elements) {
            if (content.name === elem.content.name) {
              // we should assure the element ordered by parent is always been chosen, so we set firstly chose as false to avoid the remove case
              elem.chosen = false;
              this.selectElem(elem);
            }
          }
        }
      }
    }
  }

  /**
   * On key down event listener
   * @param e
   */
  onKeyDown(e: KeyboardEvent): void {
    let caseUp = false;
    const key = e.keyCode;
    switch (key) {
      case 38: // UP
        caseUp = true;
      case 40: // DOWN
        if (!this.resultExpanded) {
          this.resultExpanded = true;
        }
        let collectionDisplayed: number[] = [];
        for (let x = 0; x < this.elements.length; x++) {
          // LIST THE displayed collection
          if (this.elements[x].displayed) {
            collectionDisplayed.push(x);
          }
        }

        let firstIndex = collectionDisplayed[0];
        let lastIndex = collectionDisplayed[collectionDisplayed.length - 1];

        for (let d = 0; d < collectionDisplayed.length; d++) {
          let realIndex = collectionDisplayed[d];
          if ((!caseUp && this.currentIndex <= realIndex) || (caseUp && this.currentIndex >= realIndex)) {
            if (this.elements[realIndex].selected) {
              this.elements[realIndex].selected = false;
              let selectedIndex = realIndex;
              if (caseUp) {
                if (d - 1 < 0) {
                  // If element unselected is the first
                  selectedIndex = lastIndex; // The last
                } else {
                  selectedIndex = collectionDisplayed[d - 1]; // The Previous
                }
              } else if (!caseUp) {
                if (d + 1 >= collectionDisplayed.length) {
                  // If element unselected is the last
                  selectedIndex = firstIndex; // The First
                } else {
                  selectedIndex = collectionDisplayed[d + 1]; // The Next
                }
              }
              this.elements[selectedIndex].selected = true;
              this.currentIndex = selectedIndex;
              this.currentElem = this.elements[selectedIndex];

              // scroll for element go into view
              let elem = document.getElementById(this.htmlId + '_' + selectedIndex);
              if (elem) {
                elem.scrollIntoView();
              }
              break; // Aaaaand break !
            }
          }
        }
        break;
      case 13: // ENTER
        if (this.resultExpanded) {
          if (!!this.currentElem && !this.currentElem.chosen) {
            this.selectElem(this.currentElem);
          }
        } else {
          this.resultExpanded = true;
        }
        break;
      case 9: // TABULATION
        this.unfocusResult();
        break;
      case 8: // BACKSPACE
        if (!this.resultExpanded) {
          this.resultExpanded = true;
        }
        // when we are in multiple mode and the chosen element exists, we pop the last element
        if (this.multiple && isArray(this.chosenElement) && this.chosenElement.length > 0 && this.filter.value === '') {
          let elemToPop = this.chosenElement[this.chosenElement.length - 1];
          if (!!elemToPop) {
            this.removeElem(elemToPop);
          }
        }
        // when we are in single mode and we have chosen element, we remove it
        if (!this.multiple && !!this.chosenElement && !isArray(this.chosenElement) && !isEmptyObject(this.chosenElement)) {
          this.removeElem(this.chosenElement);
        }
        break;
      case 27: // ESCAPE
        this.resultExpanded = false;
        break;
      default:
        this.keypressCallback.emit(this.filter.value);
        break;
    }
  }

  /**
   * Key Up event listener
   */
  onKeyUp() {
    this.keyupCallback.emit(this.filter.value);
  }

  /**
   * Input change event listener
   */
  processFilterCollection() {
    this.resultExpanded = true;
  }

  /**
   * Reset the status of all the elements and clear the chosen element
   */
  resetAll() {
    const collection = this.elements || [];
    for (let x = 0; x < collection.length; x++) {
      this.elements[x].selected = false;
      this.elements[x].chosen = false;
      this.elements[x].displayed = true;
    }
    this.fieldPlaceholder = this.originalPlaceholder;
    this.filter.setValue('');
    if (this.multiple) {
      this.internalChosenElement.next([]);
    } else {
      this.internalChosenElement.next(undefined);
    }
  }

  /**
   * Whether we have a chosen element(or not empty list)
   */
  empty() {
    return !!this.chosenElement
      ? isArray(this.chosenElement)
        ? this.chosenElement.length === 0
        : isEmptyObject(this.chosenElement)
      : true;
  }

  /**
   * Method to select an element
   * @param element
   */
  selectElem(element: ChosenElement | null | undefined) {
    if (element === null || isEmptyObject(element) || element === undefined) {
      // in case of invalid input(undefined, null or empty object), we reset all
      this.resetAll();
    } else if (!element.chosen) {
      // if this element is nor chosen, we need to keep it in chosen result
      element.chosen = true;
      if (this.multiple && isArray(this.chosenElement) && this.chosenElement.indexOf(element) < 0) {
        // when we are in multiple mode and we can't find an element in chosen result, we should add it to result
        this.chosenElement.push(element);
        this.internalChosenElement.next(this.chosenElement);
      } else if (!this.multiple) {
        // when we are in single case, and we have already chosen result, we should replace it
        if (!!this.chosenElement && !isArray(this.chosenElement) && !isEmptyObject(this.chosenElement)) {
          this.removeElem(this.chosenElement);
        }
        this.internalChosenElement.next(element);
        this.resultExpanded = false;
      }
    } else {
      this.removeElem(element);
    }
  }

  /**
   * Mouse leave event listener
   */
  unfocusResult() {
    this.resultExpanded = false;
  }

  /**
   * Focus event listener
   */
  focusResult(inputDom?: HTMLInputElement) {
    this.resultExpanded = true;
    if (!!inputDom) {
      inputDom.focus();
    }
  }

  /**
   * The method get place holder
   */
  getPlaceHolder() {
    if (this.empty() && !this.fieldDisabled) {
      return this.translateService.instant(this.originalPlaceholder);
    } else if (
      !this.empty() &&
      !this.fieldDisabled &&
      !this.multiple &&
      !!this.chosenElement &&
      !isArray(this.chosenElement) &&
      !!this.chosenElement.content &&
      !!this.chosenElement.content.name
    ) {
      return this.chosenElement.content.name;
    }
    return '';
  }

  /**
   * Remove the element in single mode
   * @param elem
   * @param inputDom
   */
  removeElemSingle(elem: ChosenElement, inputDom?: HTMLInputElement) {
    this.filter.setValue('');
    this.removeElem(elem);
    this.focusResult(inputDom);
  }

  /**
   * Remove element from chosen result
   * @param elem
   */
  removeElem(elem: ChosenElement) {
    elem.chosen = false;
    elem.selected = false;
    if (this.multiple && isArray(this.chosenElement)) {
      this.chosenElement.splice(this.chosenElement.indexOf(elem), 1);
      this.internalChosenElement.next(this.chosenElement);
      if (this.chosenElement.length === 0) {
        this.fieldPlaceholder = this.originalPlaceholder;
      }
    } else {
      this.internalChosenElement.next({} as ChosenElement);
      this.fieldPlaceholder = this.originalPlaceholder;
    }
  }

  assureArray(obj: ChosenElement | ChosenElement[] | undefined): ChosenElement[] {
    return !!obj ? (isArray(obj) ? obj : [obj]) : [];
  }

  /**
   * Applied when subject internal chosen element has new value
   * @param element
   */
  private onElementChosen(element: ChosenElement | ChosenElement[] | undefined) {
    this.chosenElement = element;
    if (!!this.chosenElement && !this.empty()) {
      // in case the chosen result is not empty
      if (this.multiple && isArray(this.chosenElement)) {
        // a new chosen result will clear input
        this.filter.setValue('');
        this.fieldPlaceholder = '';
        this.callback.emit(this.chosenElement.map(elem => elem.content));
      } else if (!this.multiple && !isArray(this.chosenElement)) {
        this.filter.setValue(this.chosenElement.content.name);
        this.fieldPlaceholder = '';
        this.callback.emit(this.chosenElement.content);
      }
    } else {
      this.filter.setValue('');
      this.callback.emit(undefined);
    }
  }

  /**
   * Method to build ChosenElement
   * @param collection
   */
  private buildChosenElement(collection: any[] | undefined): ChosenElement[] {
    return !collection
      ? []
      : collection.map(
          value =>
            ({
              content: value,
              chosen: false,
              selected: false,
              displayed: false,
            } as ChosenElement)
        );
  }

  /**
   * Method to apply filter
   * @param elements
   */
  private filterCollection(elements: ChosenElement[]): ChosenElement[] {
    let first = true;
    for (let [index, element] of elements.entries()) {
      if (element.content.name.toLowerCase().indexOf(Option.of(this.filter.value).getOrElse('').toLowerCase()) >= 0) {
        element.selected = false;
        if (first && !element.chosen) {
          first = false;
          element.selected = true;
          this.currentIndex = index;
          this.currentElem = element;
        }
        element.displayed = true;
      } else {
        element.displayed = false;
      }
    }
    return elements;
  }

  private filterChosenElement() {
    const contentNames = this.elements.map(elem => (!!elem.content.name ? elem.content.name : ''));
    if (!!this.chosenElement && this.multiple && isArray(this.chosenElement) && this.chosenElement.length > 0) {
      for (const elem of this.chosenElement) {
        if (!!elem.content.name && contentNames.indexOf(elem.content.name) < 0) {
          this.removeElem(elem);
        }
      }
    } else if (!!this.chosenElement && !this.multiple && !isArray(this.chosenElement) && !isEmptyObject(this.chosenElement)) {
      if (!!this.chosenElement.content.name && contentNames.indexOf(this.chosenElement.content.name) < 0) {
        this.removeElem(this.chosenElement);
      }
    }
  }
}
